import { mergeDeep } from "@apollo/client/utilities";
import { ApolloQueryResult, useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { useRouter } from "next/router";
import React from "react";
import { Exact, ProductIdTypeEnum } from "__generated__/globalTypes";
import styles from "components/Subscriptions/SubscriptionProduct.module.css";
import SubscriptionSidebar from "components/Subscriptions/SubscriptionSidebar";
import {
  SubscriptionProductQueryQuery,
  SubscriptionProductQueryVariables,
  SubscriptionUserQueryQuery,
  SubscriptionUserQueryVariables,
} from "components/Subscriptions/__generated__/queries";
import {
  CombinedSubscriptionProduct,
  SUBSCRIPTION_PRODUCT_QUERY,
  SUBSCRIPTION_USER_QUERY,
} from "components/Subscriptions/queries";
import { BackButton } from "components/common/Button";
import Container from "components/common/Container";
import HighlightedContent from "components/common/HighlightedContent";
import HtmlContent from "components/common/HtmlContent";
import { StripeCheckoutStatus } from "components/common/PurchaseModal/StripeCheckoutStatus";
import { SUBSCRIPTION_NAME_PLURAL } from "constants/site";
import { SUBSCRIPTIONS_URL } from "constants/urls";
import { currentUrlWithoutQueryString } from "helpers/urls";
import { usePageMeta, useRouterQuery } from "hooks";

export type SubscriptionProductParams = {
  slug: string;
};

interface ArticleProps {
  subscriptionProduct: CombinedSubscriptionProduct;
  refetch: (
    variables?: Partial<Exact<{ id: string; idType: ProductIdTypeEnum }>> | undefined
  ) => Promise<ApolloQueryResult<SubscriptionUserQueryQuery>>;
}

const Article = ({ subscriptionProduct, refetch }: ArticleProps) => {
  const productName = subscriptionProduct.name || "";

  return (
    <article className={styles.subscriptionArticleContainer}>
      <div className={styles.subscriptionArticleTextContainer}>
        <BackButton backToHref={makeUrl(SUBSCRIPTIONS_URL, { slug: "" })}>
          Back to {SUBSCRIPTION_NAME_PLURAL}
        </BackButton>
        <h1 className="font-bold text-zur-primary text-left text-3xl md:text-5xl mb-4">
          {productName}
        </h1>
        <HtmlContent>{subscriptionProduct.description}</HtmlContent>
      </div>
      <div className={styles.subscriptionSidebarContainer}>
        <HighlightedContent className={styles.subscriptionSidebar}>
          <SubscriptionSidebar product={subscriptionProduct} refetch={refetch} />
        </HighlightedContent>
      </div>
    </article>
  );
};

const SubscriptionProduct = () => {
  const router = useRouter();
  const {
    routerQuery: { slug },
  } = useRouterQuery<SubscriptionProductParams>();

  const { data: subscriptionViewData, loading: subscriptionViewLoading } = useEnhancedQuery<
    SubscriptionProductQueryQuery,
    SubscriptionProductQueryVariables
  >(SUBSCRIPTION_PRODUCT_QUERY, {
    variables: {
      id: slug,
      idType: ProductIdTypeEnum.SLUG,
    },
  });

  const {
    data: subscriptionUserData,
    loading: subscriptionUserLoading,
    refetch,
  } = useEnhancedQuery<SubscriptionUserQueryQuery, SubscriptionUserQueryVariables>(
    SUBSCRIPTION_USER_QUERY,
    {
      variables: {
        id: slug,
        idType: ProductIdTypeEnum.SLUG,
      },
    }
  );

  const subscriptionProduct = subscriptionViewData?.product || null;
  const subscriptionProductForUser = subscriptionUserData?.product || null;
  const loading = subscriptionViewLoading || subscriptionUserLoading;

  const combinedProduct = mergeDeep(subscriptionProduct, subscriptionProductForUser);

  usePageMeta(subscriptionProduct?.name, subscriptionProduct?.seo);

  return (
    <Container>
      <StripeCheckoutStatus
        onSuccessRedirectUrl={currentUrlWithoutQueryString}
        loading={loading}
        key={router.asPath}
      >
        {combinedProduct && <Article subscriptionProduct={combinedProduct} refetch={refetch} />}
      </StripeCheckoutStatus>
    </Container>
  );
};

export default SubscriptionProduct;
